import React from 'react';
import { useNavigate } from 'react-router';
import { Button, type ButtonProps } from '@naf-simba/elements-buttons';
import { useAuth0 } from '@auth0/auth0-react';
import { ProductArticleType } from '@appTypes/articleType';
import { useGTMDataLayer } from '@hooks/GTM/useGTMDataLayer';
import { EventPayload } from '@appTypes/GTM/EventPayload';
import useSelector from '@redux/typedHooks';
import { useLoginRedirect } from '@hooks/useLoginRedirect';
import { useTrackCTA } from '@utils/Tracking/CtaClick/TrackCtaClick';
import { resolveLinkProps, resolveLink } from '@lib/resolveLinkProps';
import { getHostname } from '@lib/getHostname';
import { MEMBERPAGES_URL } from '@lib/constants';

type Props = {
  productArticle: ProductArticleType;
  url: string;
  text: string;
} & Pick<ButtonProps, 'variant' | 'color'>;

export const ProductArticleButton = ({ url, text, color, variant, productArticle }: Props) => {
  const navigate = useNavigate();
  const baseUrl = useSelector((state) => state.application.baseUrl);
  // Onload Tracking
  const dataLayer = useGTMDataLayer();
  const trackCTA = useTrackCTA();
  const { isAuthenticated } = useAuth0();
  const { redirectToLoginPage } = useLoginRedirect();

  const handleClick: React.MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault();

    // Handle ecommerce tracking events
    if (productArticle.relatedProduct?.ctaButtonLink?.tracking?.event) {
      const payload: EventPayload = { event: productArticle.relatedProduct?.ctaButtonLink?.tracking?.event };
      if (
        productArticle.relatedProduct?.ctaButtonLink?.tracking?.payloadReference === 'ecommerce' &&
        productArticle.ecommerce
      ) {
        payload.ecommerce = productArticle.ecommerce;
      }
      dataLayer?.push(payload);
    }

    // Handle external / internal links and click tracking
    if (url) {
      const { isInternal, linkProps } = resolveLink({ url, baseUrl });
      if (isInternal) {
        const isLoginButton = linkProps.to.startsWith(`/${MEMBERPAGES_URL}`);

        trackCTA({
          cta_position: 'content',
          text,
          url: linkProps.to,
          cta_hostname: getHostname(url),
        });
        if (isLoginButton && !isAuthenticated) {
          redirectToLoginPage(linkProps.to);
        } else {
          navigate(linkProps.to);
        }
      } else {
        const linkUrlObject = (url && new URL(url)) || undefined;
        trackCTA({
          cta_position: 'content',
          text,
          url,
          cta_hostname: linkUrlObject?.hostname,
        });

        window.open(url);
      }
    }
  };

  const linkProps = resolveLinkProps({ url, baseUrl });

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Button color={color} variant={variant} {...linkProps} onClick={handleClick}>
      {text}
    </Button>
  );
};
